import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, MessageCircle, CheckCircle2, Share2, PackageSearch, Download, SlidersHorizontal } from "lucide-react";
import Header from "./Header";
import Footer from "./Footer";
import FloatingWhatsApp from "./FloatingWhatsApp";
import useSeo from "../hooks/useSeo";
import { wa, logClick } from "../mock";
import { ProductPhotos } from "./ProductPhotos";
import { ProductIllustration } from "./ProductIllustration";
import { Lightbox } from "./Lightbox";
import AddToListButton from "./AddToListButton";
import { STOCK_CONDITION_NOTE, specificCondition } from "../lib/condition";
import { downloadCataloguePdf } from "../lib/catalogue";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const inr = (n) => "\u20b9" + Number(n || 0).toLocaleString("en-IN");

export default function CategoryPage() {
  const { category } = useParams();
  const [items, setItems] = useState([]);
  const [related, setRelated] = useState([]);
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [lb, setLb] = useState(null);
  const [sub, setSub] = useState("All");
  const [brand, setBrand] = useState("All");
  const [priceBucket, setPriceBucket] = useState("All");
  const [showFilters, setShowFilters] = useState(false);

  useSeo({
    title: `Pre-Owned ${category} for Sale | Kapatia Pvt Ltd`,
    description: `Buy pre-owned ${category} in working condition from Kapatia Pvt Ltd — genuine assets, no repair or refurbishing, best price and quick WhatsApp enquiry. Serving Noida, Delhi, Gurgaon & Bangalore.`,
  });

  useEffect(() => {
    setLoading(true);
    fetch(`${API}/products?category=${encodeURIComponent(category)}`)
      .then((r) => r.json())
      .then((d) => setItems(Array.isArray(d) ? d : []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
    // Related: other in-stock categories + a browse strip
    fetch(`${API}/products`)
      .then((r) => r.json())
      .then((d) => {
        const all = Array.isArray(d) ? d : [];
        const others = all.filter((p) => p.category !== category && !p.sold);
        others.sort((a, b) => (b.featured ? 1 : 0) - (a.featured ? 1 : 0) || (a.sno || 0) - (b.sno || 0));
        setRelated(others.slice(0, 8));
      })
      .catch(() => setRelated([]));
    fetch(`${API}/products/categories`)
      .then((r) => r.json())
      .then((d) => setCats(Array.isArray(d) ? d.filter((c) => c !== category) : []))
      .catch(() => setCats([]));
    setSub("All"); setBrand("All"); setPriceBucket("All");
    window.scrollTo(0, 0);
  }, [category]);

  const share = async () => {
    const url = window.location.href;
    try {
      if (navigator.share) { await navigator.share({ title: `Kapatia ${category} stock`, url }); return; }
      await navigator.clipboard.writeText(url);
      setCopied(true); setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.debug("Category share/clipboard copy failed", err);
    }
  };

  // Pin featured (in-stock) items to the top for stronger ad landing pages
  const sorted = [...items].sort(
    (a, b) =>
      (b.featured && !b.sold ? 1 : 0) - (a.featured && !a.sold ? 1 : 0) ||
      (a.sold ? 1 : 0) - (b.sold ? 1 : 0) ||
      (a.sno || 0) - (b.sno || 0)
  );

  const subOptions = ["All", ...Array.from(new Set(items.map((p) => p.subcategory).filter((s) => s && s !== category))).sort()];
  const brandOptions = ["All", ...Array.from(new Set(items.map((p) => p.brand).filter(Boolean))).sort()];
  const PRICE_BUCKETS = [
    { key: "All", label: "Any price", test: () => true },
    { key: "lt5", label: "Under \u20b95,000", test: (p) => !p.price_on_request && p.price < 5000 },
    { key: "5-25", label: "\u20b95,000 \u2013 \u20b925,000", test: (p) => !p.price_on_request && p.price >= 5000 && p.price < 25000 },
    { key: "25-75", label: "\u20b925,000 \u2013 \u20b975,000", test: (p) => !p.price_on_request && p.price >= 25000 && p.price < 75000 },
    { key: "gt75", label: "Above \u20b975,000", test: (p) => !p.price_on_request && p.price >= 75000 },
  ];
  const priceTest = (PRICE_BUCKETS.find((b) => b.key === priceBucket) || PRICE_BUCKETS[0]).test;

  const filtered = sorted.filter(
    (p) => (sub === "All" || p.subcategory === sub) && (brand === "All" || p.brand === brand) && priceTest(p)
  );
  const activeFilters = (sub !== "All" ? 1 : 0) + (brand !== "All" ? 1 : 0) + (priceBucket !== "All" ? 1 : 0);
  const clearFilters = () => { setSub("All"); setBrand("All"); setPriceBucket("All"); };

  const downloadList = () => downloadCataloguePdf({
    title: `${category}${activeFilters ? " (filtered)" : ""}`,
    items: filtered.map((p) => ({
      code: p.code, name: p.name, condition: p.condition, qty_available: p.qty,
      price: p.price, price_on_request: p.price_on_request, warranty_display: p.warranty_display, id: p.id,
    })),
    enquiry: false,
  });

  return (
    <div className="App public-site">
      <Header />
      {/* Category hero */}
      <section className="relative overflow-hidden grid-backdrop"
        style={{ background: "linear-gradient(135deg, rgba(255,248,236,0.98), rgba(255,253,247,0.9)), radial-gradient(circle at 84% 18%, rgba(245,180,0,0.22), transparent 28%)" }}>
        <div className="container relative z-10 py-14">
          <Link to="/#stock" className="inline-flex items-center gap-2 font-heading text-[14px] font-bold" style={{ color: "var(--orange-700)" }}>
            <ArrowLeft size={16} /> All stock
          </Link>
          <div className="mt-4 flex flex-wrap items-end justify-between gap-4">
            <div>
              <p className="eyebrow">Pre-owned {category}</p>
              <h1 data-testid="category-heading" className="mt-2 font-heading font-semibold leading-[1]" style={{ color: "var(--green-950)", fontSize: "clamp(38px,6vw,64px)" }}>
                {category} <span style={{ color: "var(--orange-700)" }}>for sale</span>
              </h1>
              <p className="mt-3 max-w-[620px] text-[17px] font-medium" style={{ color: "#31463a" }}>
                {loading ? "Loading…" : `${items.length} ${category.toLowerCase()} item${items.length === 1 ? "" : "s"} in stock. ${STOCK_CONDITION_NOTE} Enquire on WhatsApp for best price.`}
              </p>
              <p data-testid="warranty-note" className="mt-2 inline-block rounded-md px-2.5 py-1 text-[12.5px] font-semibold" style={{ background: "var(--green-100)", color: "var(--green-900)" }}>
                Pre-owned / open-box company assets · 1 month testing warranty unless otherwise stated.
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2.5">
              <button onClick={downloadList} data-testid="category-download"
                      className="kp-button inline-flex items-center gap-2 rounded-full border bg-white px-5 py-3 font-heading font-bold transition-transform hover:-translate-y-0.5"
                      style={{ borderColor: "var(--line)", color: "var(--green-900)" }}>
                <Download size={17} /> Download list
              </button>
              <button onClick={share} data-testid="category-share"
                      className="kp-button inline-flex items-center gap-2 rounded-full px-5 py-3 font-heading font-bold transition-transform hover:-translate-y-0.5"
                      style={{ background: "var(--yellow-500)", color: "var(--green-950)" }}>
                <Share2 size={17} /> {copied ? "Link copied!" : "Share page"}
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Filters */}
      {!loading && items.length > 0 && (
        <section className="border-b" style={{ background: "#fff", borderColor: "var(--line)" }}>
          <div className="container py-3">
            <div className="flex items-center justify-between gap-3">
              <button onClick={() => setShowFilters((v) => !v)} data-testid="category-filter-toggle"
                      className="inline-flex items-center gap-2 font-heading text-[13.5px] font-bold" style={{ color: "var(--green-900)" }}>
                <SlidersHorizontal size={16} /> Filters{activeFilters ? ` (${activeFilters})` : ""}
              </button>
              <span className="text-[13px] font-semibold" data-testid="category-result-count" style={{ color: "var(--muted)" }}>
                {filtered.length} of {items.length} shown
              </span>
            </div>
            <div className={`${showFilters ? "grid" : "hidden"} mt-3 gap-3 sm:grid sm:grid-cols-3`}>
              {subOptions.length > 1 && (
                <label className="block">
                  <span className="mb-1 block text-[11.5px] font-bold uppercase tracking-wide" style={{ color: "var(--muted)" }}>Type</span>
                  <select value={sub} onChange={(e) => setSub(e.target.value)} data-testid="filter-subcategory"
                          className="w-full rounded-lg border px-3 py-2.5 font-sans text-[14px]" style={{ borderColor: "var(--line)", background: "#fff", color: "var(--ink)" }}>
                    {subOptions.map((o) => <option key={o} value={o}>{o === "All" ? "All types" : o}</option>)}
                  </select>
                </label>
              )}
              {brandOptions.length > 1 && (
                <label className="block">
                  <span className="mb-1 block text-[11.5px] font-bold uppercase tracking-wide" style={{ color: "var(--muted)" }}>Brand</span>
                  <select value={brand} onChange={(e) => setBrand(e.target.value)} data-testid="filter-brand"
                          className="w-full rounded-lg border px-3 py-2.5 font-sans text-[14px]" style={{ borderColor: "var(--line)", background: "#fff", color: "var(--ink)" }}>
                    {brandOptions.map((o) => <option key={o} value={o}>{o === "All" ? "All brands" : o}</option>)}
                  </select>
                </label>
              )}
              <label className="block">
                <span className="mb-1 block text-[11.5px] font-bold uppercase tracking-wide" style={{ color: "var(--muted)" }}>Price</span>
                <select value={priceBucket} onChange={(e) => setPriceBucket(e.target.value)} data-testid="filter-price"
                        className="w-full rounded-lg border px-3 py-2.5 font-sans text-[14px]" style={{ borderColor: "var(--line)", background: "#fff", color: "var(--ink)" }}>
                  {PRICE_BUCKETS.map((b) => <option key={b.key} value={b.key}>{b.label}</option>)}
                </select>
              </label>
            </div>
            {activeFilters > 0 && (
              <button onClick={clearFilters} data-testid="filter-clear" className="mt-2 font-heading text-[12.5px] font-bold" style={{ color: "var(--orange-700)" }}>
                Clear filters
              </button>
            )}
          </div>
        </section>
      )}

      {/* Grid */}
      <section className="py-16" style={{ background: "var(--paper)" }}>
        <div className="container">
          {loading ? (
            <p className="text-center" style={{ color: "var(--muted)" }}>Loading inventory…</p>
          ) : items.length === 0 ? (
            <div className="grid place-items-center gap-3 py-16 text-center">
              <PackageSearch size={40} style={{ color: "var(--muted)" }} />
              <p className="font-heading font-bold" style={{ color: "var(--green-950)" }}>No {category} in stock right now</p>
              <Link to="/#contact" className="font-heading font-bold" style={{ color: "var(--orange-700)" }}>Send an enquiry →</Link>
            </div>
          ) : filtered.length === 0 ? (
            <div className="grid place-items-center gap-3 py-16 text-center" data-testid="category-no-match">
              <PackageSearch size={40} style={{ color: "var(--muted)" }} />
              <p className="font-heading font-bold" style={{ color: "var(--green-950)" }}>No items match these filters</p>
              <button onClick={clearFilters} className="font-heading font-bold" style={{ color: "var(--orange-700)" }}>Clear filters →</button>
            </div>
          ) : (
            <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
              {filtered.map((p) => (
                <article key={p.id} className="group flex flex-col overflow-hidden rounded-2xl border bg-white transition-transform hover:-translate-y-1.5"
                         style={{ borderColor: "var(--line)", boxShadow: "0 12px 30px rgba(45,106,79,0.08)" }}>
                  <div className="relative h-40 overflow-hidden" style={{ background: "var(--green-100)" }}>
                    <ProductPhotos p={p} />
                    {p.featured && !p.sold && (
                      <span className="absolute left-3 top-3 rounded-full px-2.5 py-1 font-heading text-[11px] font-extrabold text-white"
                            style={{ background: "linear-gradient(135deg, #ff3f1f, var(--orange-500))" }}>★ Featured</span>
                    )}
                    {p.sold ? (
                      <span className="absolute right-3 top-3 rounded-full px-3 py-1 font-heading text-[11px] font-extrabold text-white" style={{ background: "var(--orange-700)" }}>SOLD</span>
                    ) : p.qty > 1 ? (
                      <span className="absolute right-3 top-3 rounded-full px-2.5 py-1 font-heading text-[11px] font-bold" style={{ background: "var(--yellow-500)", color: "var(--green-950)" }}>{p.qty} in stock</span>
                    ) : null}
                  </div>
                  <div className="flex flex-1 flex-col p-4">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-[13px] font-extrabold uppercase tracking-wide" style={{ color: "var(--orange-800)" }}>{p.brand}</span>
                      {p.code && <span data-testid={`product-code-${p.id}`} className="rounded-full px-2 py-0.5 font-heading text-[11px] font-bold" style={{ background: "var(--green-100)", color: "var(--green-900)" }}>{p.code}</span>}
                    </div>
                    <h3 className="mt-1 font-sans text-[15px] font-medium leading-relaxed line-clamp-3" style={{ color: "var(--ink)" }}>
                      <Link to={`/product/${p.id}`} data-testid={`product-link-${p.id}`} className="hover:underline">{p.name}</Link>
                    </h3>
                    {specificCondition(p) && (
                      <span data-testid={`category-condition-${p.id}`} className="mt-3 inline-flex w-max items-center gap-1 text-[12px] font-bold" style={{ color: "var(--bright-green-strong)" }}>
                        <CheckCircle2 size={13} /> {specificCondition(p)}
                      </span>
                    )}
                    {p.review_status === "Details to confirm" && (
                      <span data-testid={`category-review-${p.id}`} className="mt-2 inline-flex w-max items-center rounded-md px-2 py-0.5 text-[11px] font-bold" style={{ background: "#fff4e5", color: "var(--orange-700)" }}>
                        Details to confirm — verify on enquiry
                      </span>
                    )}
                    <div className="mt-auto pt-3">
                      {p.price_on_request
                        ? <span className="font-heading text-[15px] font-extrabold" style={{ color: "var(--green-800)" }}>Price on request</span>
                        : <span className="font-heading text-[18px] font-extrabold" style={{ color: "var(--orange-700)" }}>{inr(p.price)}</span>}
                    </div>
                    <div className="mt-3 flex items-center gap-2">
                      <a href={wa(`Hi Kapatia, I'm interested in ${p.code ? p.code + ": " : ""}${p.name}${p.brand ? ` (${p.brand})` : ""}. Is it available?${p.excludes ? `\nNote: ${p.excludes}` : ""}`)}
                         onClick={() => logClick(p, `category:${category}`)}
                         data-product-code={p.code} data-product-category={p.category}
                         target="_blank" rel="noopener noreferrer" data-testid={`category-enquire-${p.id}`}
                         className="kp-button kp-button--compact inline-flex flex-1 items-center justify-center gap-2 rounded-full px-4 py-2.5 font-heading text-[13.5px] font-bold text-white transition-transform hover:-translate-y-0.5"
                         style={{ background: "linear-gradient(135deg, var(--bright-green), var(--bright-green-strong))" }}>
                        <MessageCircle size={15} /> Enquire
                      </a>
                      <AddToListButton product={p} />
                    </div>
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Similar items to keep buyers browsing */}
      {(related.length > 0 || cats.length > 0) && (
        <section className="py-16" style={{ background: "var(--cream)" }}>
          <div className="container">
            {cats.length > 0 && (
              <div className="mb-10">
                <h2 className="font-heading text-[22px] font-extrabold" style={{ color: "var(--green-950)" }}>Browse other categories</h2>
                <div className="mt-4 flex flex-wrap gap-2.5">
                  {cats.map((c) => (
                    <Link key={c} to={`/stock/${encodeURIComponent(c)}`} data-testid={`category-browse-${c.toLowerCase()}`}
                          className="kp-button kp-button--compact inline-flex rounded-full border bg-white px-4 py-2 font-heading text-[13.5px] font-bold transition-transform hover:-translate-y-0.5"
                          style={{ borderColor: "var(--line)", color: "var(--green-950)" }}>{c}</Link>
                  ))}
                  <Link to="/store" data-testid="category-browse-store"
                        className="kp-button kp-button--compact inline-flex rounded-full border bg-white px-4 py-2 font-heading text-[13.5px] font-bold transition-transform hover:-translate-y-0.5"
                        style={{ borderColor: "var(--line)", color: "var(--green-950)" }}>All store categories</Link>
                </div>
              </div>
            )}
            {related.length > 0 && (
              <>
                <h2 className="font-heading text-[22px] font-extrabold" style={{ color: "var(--green-950)" }}>You may also like</h2>
                <div className="no-scrollbar mt-4 flex gap-4 overflow-x-auto pb-2">
                  {related.map((p) => (
                    <Link key={p.id} to={`/stock/${encodeURIComponent(p.category)}`}
                          className="flex w-[240px] flex-shrink-0 flex-col overflow-hidden rounded-2xl border bg-white transition-transform hover:-translate-y-1"
                          style={{ borderColor: "var(--line)", boxShadow: "0 12px 30px rgba(45,106,79,0.08)" }}>
                      <div className="relative h-32 overflow-hidden" style={{ background: "var(--green-100)" }}>
                        {p.img ? (
                          <>
                            <img src={p.img} alt={p.name} loading="lazy"
                                 onClick={(e) => { e.preventDefault(); e.stopPropagation(); setLb({ images: (p.images && p.images.length ? p.images : [p.img]).filter(Boolean), alt: p.name, url: `${window.location.origin}/product/${p.id}`, credit: (p.image_credit || p.image_kind === "reference") ? { text: p.image_credit || "", source: p.image_source_url || "", license: p.image_license || "", licenseUrl: p.image_license_url || "", changes: (p.image_changes && !/^\s*unmodified\b/i.test(p.image_changes)) ? p.image_changes : "", reference: p.image_kind === "reference" } : null }); }}
                                 className={`h-full w-full cursor-zoom-in ${p.image_kind === "reference" ? "object-contain" : "object-cover"}`} />
                            {p.image_kind === "reference" && (
                              <span className="absolute bottom-1.5 left-1.5 z-10 rounded px-1.5 py-0.5 text-[9.5px] font-bold text-white" style={{ background: "rgba(13,58,43,0.82)" }}>Model reference image</span>
                            )}
                          </>
                        ) : (
                          <ProductIllustration p={p} />
                        )}
                        <span className="absolute left-3 top-3 z-10 rounded-full px-2.5 py-1 font-heading text-[11px] font-bold text-white" style={{ background: "var(--bright-green-strong)" }}>{p.category}</span>
                      </div>
                      <div className="flex flex-1 flex-col p-4">
                        <span className="text-[11px] font-bold uppercase tracking-wide" style={{ color: "var(--orange-700)" }}>{p.brand}</span>
                        <h3 className="mt-1 font-sans text-[14px] font-medium leading-relaxed" style={{ color: "var(--ink)" }}>{p.name}</h3>
                        <span className="mt-2 font-heading text-[15px] font-extrabold" style={{ color: "var(--orange-700)" }}>
                          {p.price_on_request ? "Price on request" : inr(p.price)}
                        </span>
                      </div>
                    </Link>
                  ))}
                </div>
              </>
            )}
          </div>
        </section>
      )}

      <Footer />
      <FloatingWhatsApp />
      {lb && <Lightbox images={lb.images} alt={lb.alt} shareUrl={lb.url} credit={lb.credit} onClose={() => setLb(null)} />}
    </div>
  );
}
